/* eslint-disable react/no-array-index-key */
import { Form, message, Space } from 'antd';
import { useState, VFC } from 'react';
import { SendDataModal } from 'components/Modal/SendDataModal';
import { useServices } from 'services/useServices';
import { v4 } from 'uuid';
import {
  selectReplaceMeterPayload,
  selectReplaceMeterPhotos,
  useReplaceMeterStore,
} from 'routes/meter_replacement/useStore';
import { useReplaceMeterStepsStore } from 'routes/meter_replacement/useStepsStore';
import { useNavigate, useParams } from 'react-router-dom';
import { useSessionStore } from 'store/session';
import { captureException } from '@sentry/react';
import { useIntl } from 'react-intl';
import { InventoryMeterOutputDescriptions } from 'components/descriptions/InventoryMeterOutput';
import { OldValuesDescriptions } from './descriptions/OldValues';
import { NewValuesDescriptions } from './descriptions/NewValues';
import { DetailsDescriptions } from './descriptions/Details';
import { REVIEW_STEP_FORM_ID } from './constants';
import { ReviewStepContent as messages } from './messages';
import { useMeterModelSuggestionsStore } from '../../../../store/useMeterModelSuggestionsStore';

export const ReviewStepContent: VFC = () => {
  const { id } = useParams();
  const inventoryMeterId = parseInt(id ?? '');
  const { apiKey, email } = useSessionStore();

  const {
    api: { inventoryMetersApiClient },
    indexedDb: { replaceMeterDb },
  } = useServices();
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const {
    isSendingData,
    setIsSendingData,
    reset: resetReplaceMeterStore,
    setQueuedMetersCount,
    outputs,
  } = useReplaceMeterStore();
  const { reset: resetReplaceMeterStepsStore } = useReplaceMeterStepsStore();
  const { reset: resetMeterModelSuggestionsStore } = useMeterModelSuggestionsStore();
  const data = useReplaceMeterStore(selectReplaceMeterPayload);
  const formPhotos = useReplaceMeterStore(selectReplaceMeterPhotos);
  const intl = useIntl();

  const handleOnSubmit = async () => {
    setIsSendingData(true);
    await inventoryMetersApiClient
      .replace({ data: { ...data, replaced_by: email, id: inventoryMeterId }, formPhotos, inventoryMeterId })
      .then(() => {
        message.success(intl.formatMessage(messages.recordSentSuccess));
        resetReplaceMeterStore();
        resetReplaceMeterStepsStore();
        resetMeterModelSuggestionsStore();

        navigate(`/`);
      })
      .catch(error => {
        message.error(intl.formatMessage(messages.recordSentError));
        console.error(error);
        captureException(error);
      })
      .finally(() => {
        setIsSendingData(false);
      });
  };

  const handleSaveRecord = async () => {
    setIsModalVisible(false);
    await replaceMeterDb.insertData([
      {
        id: v4(),
        locationApiKey: apiKey ?? '',
        replacementMeter: { data, formPhotos, inventoryMeterId },
        wasSynced: 0,
      },
    ]);
    const metersCount = await replaceMeterDb.countNotSynchronized();
    setQueuedMetersCount(metersCount);
    message.success(intl.formatMessage(messages.queuedMetersSentSuccess));

    resetReplaceMeterStore();
    resetReplaceMeterStepsStore();
    resetMeterModelSuggestionsStore();
    navigate(`/`);
  };

  return (
    <>
      <Form id={REVIEW_STEP_FORM_ID} layout="horizontal" onFinish={() => setIsModalVisible(true)} />
      <Space direction="vertical">
        <OldValuesDescriptions />
        <NewValuesDescriptions />

        {outputs?.map((output, index) => (
          <InventoryMeterOutputDescriptions key={index} order={index + 1} output={output} />
        ))}

        <DetailsDescriptions />
      </Space>

      <SendDataModal
        isSendingData={Boolean(isSendingData)}
        isVisible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onHandleSubmit={handleOnSubmit}
        onSaveRecord={handleSaveRecord}
      />
    </>
  );
};
